import React, { useState, useCallback, useMemo, useEffect } from 'react';
import styles from './NewCoverCard.module.scss';
import { SllLesson } from '@src/api/graphql-codegen';
import { getMediaAbsolutePath } from '@src/services/File';
import { useMediaQuery } from 'react-responsive';
import { ClockCircleOutlined } from '@ant-design/icons';
import IconSkill from 'src/assets/lessonicons/icon-competency.svg?react';
import IconTier from 'src/assets/lessonicons/icon-tier.svg?react';
import IconLesson from 'src/assets/lessonicons/icon-lesson.svg?react';
import IconMood from 'src/assets/lessonicons/icon-mood.svg?react';
import CompleteIcon from 'src/assets/icons/select_mark.svg?react';
import IconPhases from 'src/assets/lessonicons/icon-phases.svg?react';
import { categoryTiersOptions, countryOptions, IconExtension, IconProjRequired, lessonTypeLabels, IconDirection, IconTargetArea, IconInfo } from '@src/constants';
import { LordIcon } from '@src/components/lord-icon/lord-icon';
import { createContentLabel } from '@src/utils/labelUtils';
import { LESSON_TYPE_CODE, LessonCardContext, resolveLessonCardBarLabel } from '@src/utils/lessonCardTopBar';
import IconVideo from 'src/assets/icons/cover-video.svg?react';
import IconAudio from 'src/assets/icons/cover-audio.svg?react';
import IconSlide from 'src/assets/icons/cover-slide.svg?react';

type Props = {
  content: SllLesson;
  type?: string;
  source?: string;
  target?: string;
  country: string;
  handleOpenContent?: (e: React.MouseEvent<HTMLDivElement>, content: SllLesson) => void;
  programID?: string | "";
  handleMovableNewCover?: (inNew: boolean) => void;
  isAUuser: boolean;
  isCaregiver?: boolean;
  /** Surface the card sits on; drives the AU purple-bar text (OP-3134). Defaults to a regular program. */
  cardContext?: LessonCardContext;
}

function NewCoverCard(props: Props) {
  const [isHovered, setIsHovered] = useState(false);
  const { content, programID, country, isAUuser, isCaregiver=false, cardContext='program', handleOpenContent } = props;
  const isMobile = useMediaQuery({ maxWidth: 915 });
  const {
    id,
    name,
    categories,
    duration,
    suggestedDuration,
    progress,
    coverImage,
    isComingSoon,
    comingSoonAt,
    isNew,
    isUpdated,
    newLabelPosition,
    updatedLabelPosition,
    comingSoonLabelPosition,
    competency,
    lessonDetails,
    lessonType,
  } = content;

  const lessonCategories = useMemo(() => JSON.parse(categories || '[]'), [categories]);
  const skill = useMemo(() => JSON.parse(competency || '[]'), [competency]);

  const [tiers, setTiers] = useState('')
  const [mood, setMood] = useState('')
  const [auSkill, setAuSkill] = useState('')
  const [direction, setDirection] = useState('')
  const [targetArea, setTargetArea] = useState('')
  const [containSlides, setContainSlides] = useState('')
  const [containVideo, setContainVideo] = useState('')
  const [containAudio, setContainAudio] = useState('')

  useEffect(() => {
    if (lessonCategories['Tiers']) {
      // TO DO: currently, we have bad datas for the Tiers in the DB. We have to udpate the Tiers manually at the edit-lesson
      // After DB update, we should revert to the prev version that is commented out.
      const tiersOption = categoryTiersOptions.find(o => (`${o.value}` === `${lessonCategories['Tiers']}` || o.oldValues?.includes(lessonCategories['Tiers'])))
      setTiers(tiersOption?.value || '')
      // setTiers(lessonCategories['Tiers'])
    }
    if (lessonCategories['Mood']) {
      setMood(lessonCategories['Mood'])
    }
    if (lessonCategories['Style']) {
      // TO DO: currently, we have bad datas for the style options in the DB. We have to udpate the style manually at the edit-lesson
      // After DB update, we should revert to the prev version that is commented out.
      // const styleOption = categoryStyleOptions.find(o => (`${o.value}` === `${lessonCategories['Style']}` || o.oldValues?.includes(lessonCategories['Style'])))
      // setStyle(styleOption?.value || '')
      // setStyle(lessonCategories['Style'])
    }
    if (lessonCategories['Skill']) {
      setAuSkill(lessonCategories['Skill'])
    }
    if (lessonCategories['Direction']) {
      setDirection(lessonCategories['Direction'])
    }
    if (lessonCategories['Target Area']) {
      setTargetArea(lessonCategories['Target Area'])
    }
    if (lessonCategories['ContainSlides']) {
      setContainSlides(lessonCategories['ContainSlides'])
    }
    if (lessonCategories['ContainVideo']) {
      setContainVideo(lessonCategories['ContainVideo'])
    }
    if (lessonCategories['ContainAudio']) {
      setContainAudio(lessonCategories['ContainAudio'])
    }
  }, [lessonCategories])

  const onClickUnit = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
    handleOpenContent?.(e, content)
  }, [handleOpenContent, content])

  const label = createContentLabel({
    isNew,
    isUpdated,
    isComingSoon,
    comingSoonAt,
    newLabelPosition,
    updatedLabelPosition,
    comingSoonLabelPosition
  });

  const progressBar = (
    <div className={styles.progressBackBar} style={{borderBottomLeftRadius: '0', borderBottomRightRadius: '0'}}>
      <div className={styles.progressBar} style={{width: `${progress}%`, backgroundColor: progress === 100 ? "#62C590" : "#62C590", borderBottomLeftRadius: '0' }}>
      </div>
    </div>
  )

  const completedLabel = useMemo(() => {
    if (progress === 100) {
      return (
        <div className={styles.completeLabel}>
          <div>
            <div className={styles.completeIcon}>
              <CompleteIcon width={10} height={10}/>
            </div>
            <span>{'Completed'}</span>
          </div>
        </div>
      )
    }
    return null
  }, [progress])

  // Purple bar text, or null when the bar is hidden and the duration pill stands alone.
  const barLabel = resolveLessonCardBarLabel({ lessonType, isAUuser, context: cardContext });
  const isResource = lessonType === LESSON_TYPE_CODE.RESOURCE;

  const suggestBar = (
    <div className={styles.lessonSuggestView}>
      {barLabel ?
        <div className={`${styles.lessonsView} ${isResource ? styles.durationViewAlone : ''}`}>
          <IconLesson width={16} />
          <div className={`${styles.lessonsText} ${barLabel === lessonTypeLabels[LESSON_TYPE_CODE.POSITIVE_PATHWAYS] ? styles.narrowText : ''}`}>
            {barLabel}
          </div>
        </div> : null
      }
      {!isResource && ((country === countryOptions[1].label && suggestedDuration && Number(suggestedDuration) > 0) || (country !== countryOptions[1].label && duration && duration > 0)) ? <div className={`${styles.durationView}
      ${barLabel ? '' : styles.durationViewAlone}`}>
        <ClockCircleOutlined className={styles.clockIcon}/>
        <div className={styles.durationText}>{country === countryOptions[1].label ? suggestedDuration : duration}&nbsp;<span>min</span></div>
      </div> : null}
    </div>

  )

  const bottomView = (lessonType: string) => {
    switch (lessonType) {
      case "4": {
        return (
          <>
            <div className={styles.divider} />
            <div className={styles.onboardingDetailView}>
              <LordIcon src={IconInfo}
                isHover={true} 
                colors={{
                  primary: '#AF77B8',
                  secondary: '#FFB073'
                }}
                size={isMobile ? 10: 20}
                target='.CoverUnit_tagBox__nusN2' />
              <span>{lessonDetails}</span>
            </div>
          </>
        )
      }
      case "5": {
        return (
          <div className={[styles.tagBox, styles['skill']].join(' ')} style={{marginTop: 8}}>
            <span>Downloadable Resource</span>
          </div>
        )
      }
      case "7": {
        return (
          <>
            <div className={styles.divider} />
            <div className={styles.categoryView}>
              <div className={[styles.tagBox, styles['direction']].join(' ')}>
                <LordIcon src={IconDirection}
                  isHover={true} 
                  colors={{
                    primary: '#274649',
                    secondary: '#FFB073'
                  }}
                  size={isMobile ? 10: 16}
                  target='.CoverUnit_tagBox__nusN2' />
                <span>{direction}</span>
              </div>
              <div className={[styles.tagBox, styles['targetArea']].join(' ')}>
                <LordIcon src={IconTargetArea}
                  isHover={true} 
                  colors={{
                    primary: '#274649',
                    secondary: '#FFB073'
                  }}
                  size={isMobile ? 10: 16}
                  target='.CoverUnit_tagBox__nusN2' />
                <span>{targetArea}</span>
              </div>
            </div>
          </>
        )
      }
      case "0":
      case "1":
      case "2":
      case "3":
      case "6": {
        return (
          <>
            {isAUuser ? (auSkill ? <div className={[styles.tagBox, styles['skill']].join(' ')} style={{marginTop: 8}}>
              <IconSkill width={isMobile ? 10: 16} />
              <div className={styles.title}>Skill: </div><span>{auSkill}</span>
            </div> : null) :
            (skill.length > 0 ? <div className={[styles.tagBox, styles['skill']].join(' ')} style={{marginTop: 8}}>
              <IconSkill width={isMobile ? 10: 16} />
              <div className={styles.title}>Competency: </div><span>{skill[0]}</span>
            </div> : null)}
            <div className={styles.divider} />
            {(containSlides === "0" || containVideo === "1" || containAudio === "2") && <div className={styles.categoryView}>
              {containSlides === "0" && <div className={styles.techBox}><IconSlide width={isMobile ? 16: 24} /><div className={styles.title}>Contains Slide Presentation</div></div>}
              {containVideo === "1" && <div className={styles.techBox}><IconVideo width={isMobile ? 16: 24} /><div className={styles.title}>Contains Video</div></div>}
              {containAudio === "2" && <div className={styles.techBox}><IconAudio width={isMobile ? 16: 24} /><div className={styles.title}>Contains Audio</div></div>}
            </div>}
            <div className={styles.categoryView}>
              {tiers ? <div className={[styles.tagBox, styles['tiers']].join(' ')}>
                <IconTier width={isMobile ? 10: 16} />
                <span>{tiers}</span></div> : null}
              {mood ? <div className={[styles.tagBox, styles['mood']].join(' ')}>
              <IconMood width={isMobile ? 10: 16} />
              <span>{mood}</span></div> : null}
            </div>
          </>
        )
      }
    }
  }

  return (
    <div id={`${programID}_${id}`} className={[isHovered ? styles.newCoverHoveredCard : styles.newCoverCard].join(' ')}
      onClick={onClickUnit} onMouseOver={() => setIsHovered(true)} onMouseLeave={() => setIsHovered(false)}>
      <div className={styles.topView} style={{ position: 'relative' }}>
        <div className={styles.unitBackground}>
          <div className={styles.unitImage}
            style={{backgroundImage: `url(${coverImage ? getMediaAbsolutePath(coverImage) : ''})`}}>
              {completedLabel}
          </div>
        </div>
        {label}
        {(progress && progress > 0) ? progressBar : null}
        {!isComingSoon ? suggestBar : null}
      </div>
      <div className={styles.bottomView}>
        <div className={styles.unitName}>{name}</div>
        {isCaregiver ? bottomView('7') : bottomView(lessonType ?? '')}
      </div>
    </div>
  );
}

export default NewCoverCard;
